import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "shared/components/ui/table";
import { WrapperListArtifactType } from "types/AssistantTypes";
import styles from "./finecoWrapperListArtifact.module.css";

type Props = {
  data: WrapperListArtifactType;
  onSend: (text: string) => void;
};

type MicroSort = "default" | "asc" | "desc";

/**
 * «Wrapper» list (FAM funds of funds) returned by the `list_wrappers` tool
 * (issue #62). Clicking a row asks the agent for that fund's Scheda prodotto.
 * The Microcategoria header sorts by that column (issue #95); the default
 * order is the tool's five-group ordering.
 */
const FinecoWrapperListArtifact = ({ data, onSend }: Props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { t } = useTranslation();
  const [microSort, setMicroSort] = useState<MicroSort>("default");

  const wrappers = useMemo(() => {
    const rows = data?.wrappers ?? [];
    if (microSort === "default") return rows;
    const dir = microSort === "desc" ? -1 : 1;
    // Direction applies to microcategoria only (the nome tie-break stays
    // ascending); blank microcategoria sorts last in both directions.
    return [...rows].sort((a, b) => {
      const am = a.microcategoria ?? "";
      const bm = b.microcategoria ?? "";
      if (!am !== !bm) return am ? -1 : 1;
      return dir * am.localeCompare(bm) || a.nome.localeCompare(b.nome);
    });
  }, [data?.wrappers, microSort]);

  const cycleMicroSort = () =>
    setMicroSort((s) => (s === "default" ? "asc" : s === "asc" ? "desc" : "default"));

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: colorsConfig.grayLight,
        color: colorsConfig.finecoBlack,
      }}
    >
      <h3 className={styles.title} style={{ color: colorsConfig.portfolioTitle }}>
        {t("assistant.wrappers.title")}
      </h3>
      {data?.data && (
        <p className={styles.subtitle}>
          {t("assistant.wrappers.subtitle", { date: data.data })}
        </p>
      )}
      {wrappers.length === 0 ? (
        <p className={styles.subtitle}>{t("assistant.wrappers.empty")}</p>
      ) : (
        <>
          <p className={styles.hint}>{t("assistant.wrappers.hint")}</p>
          <div className={styles.tableContainer}>
            <Table className={styles.table}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("assistant.wrappers.name")}</TableHead>
                  <TableHead>{t("assistant.wrappers.isin")}</TableHead>
                  <TableHead>{t("assistant.wrappers.info")}</TableHead>
                  <TableHead
                    className={styles.sortableHead}
                    onClick={cycleMicroSort}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        cycleMicroSort();
                      }
                    }}
                    aria-sort={
                      microSort === "asc"
                        ? "ascending"
                        : microSort === "desc"
                          ? "descending"
                          : "none"
                    }
                  >
                    {t("assistant.wrappers.microcategoria")}
                    <span className={styles.sortIndicator}>
                      {microSort === "asc" ? "▲" : microSort === "desc" ? "▼" : "⇅"}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wrappers.map((w) => (
                  <TableRow
                    key={w.isin}
                    className={styles.row}
                    onClick={() =>
                      onSend(
                        t("assistant.wrappers.productCardMessage", {
                          isin: w.isin,
                        })
                      )
                    }
                  >
                    <TableCell>{w.nome}</TableCell>
                    <TableCell>{w.isin}</TableCell>
                    <TableCell>
                      {w.has_informazioni_gestore ? (
                        <span className={styles.available}>
                          ✓ {t("assistant.wrappers.available")}
                        </span>
                      ) : (
                        <span className={styles.unavailable}>
                          {t("assistant.wrappers.unavailable")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{w.microcategoria ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default FinecoWrapperListArtifact;
